"use client";

import { motion } from "framer-motion";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { cn } from "@/lib/utils";
import { ArrowRight, Check, Hand } from "lucide-react";

export function Eyebrow({ children, right }: { children: ReactNode; right?: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="eyebrow">{children}</span>
      {right ? <span className="font-mono text-[11px] text-muted-foreground">{right}</span> : null}
    </div>
  );
}

export function Headline({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <motion.h1
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className={cn("mt-2 text-[clamp(1.5rem,6vw,2rem)] lg:text-[2.35rem]", className)}
    >
      {children}
    </motion.h1>
  );
}

export function Sub({ children }: { children: ReactNode }) {
  return (
    <p className="mt-2 text-[0.88rem] leading-relaxed text-muted-foreground lg:mt-3 lg:text-[1rem]">
      {children}
    </p>
  );
}

export function Panel({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn("panel grain p-3.5 lg:p-5", className)}>{children}</div>;
}

export function DoPill({ children }: { children: ReactNode }) {
  return (
    <div className="mt-3 inline-flex items-center gap-2 rounded-full border border-primary/35 bg-primary/10 px-3 py-1 font-mono text-[11px] tracking-wide text-primary-soft">
      <Hand className="size-3.5" />
      {children}
    </div>
  );
}

export function Teach({
  children,
  chip,
  good = true,
}: {
  children: ReactNode;
  chip?: string | undefined;
  good?: boolean;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={cn(
        "mt-3 rounded-xl border p-3.5 text-[0.85rem] leading-relaxed lg:p-4 lg:text-sm",
        good
          ? "border-accent/30 bg-accent/8 text-foreground"
          : "border-primary/30 bg-primary/8 text-foreground",
      )}
    >
      {children}
      {chip ? (
        <div className="mt-3 inline-block rounded-full border border-hairline bg-surface-2 px-2.5 py-1 font-mono text-[10px] tracking-wide text-muted-foreground">
          {chip}
        </div>
      ) : null}
    </motion.div>
  );
}

export function Source({ children }: { children: ReactNode }) {
  return (
    <p className="mt-2 font-mono text-[10px] uppercase tracking-wider text-muted-foreground/70">
      Source: {children}
    </p>
  );
}

type OptState = "idle" | "picked" | "good" | "bad" | "locked";

export function Option({
  children,
  hint,
  state = "idle",
  onClick,
  disabled,
  delay = 0,
}: {
  children: ReactNode;
  hint?: string | undefined;
  state?: OptState | undefined;
  onClick?: (() => void) | undefined;
  disabled?: boolean | undefined;
  delay?: number | undefined;
}) {
  return (
    <motion.button
      type="button"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.35 }}
      whileTap={{ scale: disabled ? 1 : 0.985 }}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "flex w-full items-center justify-between gap-3 rounded-xl border p-3 text-left transition-colors lg:p-4",
        state === "idle" && "border-hairline bg-surface hover:border-primary/45 hover:bg-surface-2",
        state === "picked" && "border-primary bg-primary/15",
        state === "good" && "border-accent bg-accent/15",
        state === "bad" && "border-destructive/60 bg-destructive/12",
        state === "locked" && "border-hairline bg-surface/60 opacity-55",
      )}
    >
      <span>
        <span className="block text-[0.9rem] font-semibold leading-snug lg:text-[0.97rem]">
          {children}
        </span>
        {hint ? (
          <span className="mt-0.5 block text-[0.8rem] text-muted-foreground">{hint}</span>
        ) : null}
      </span>
      {(state === "good" || state === "picked") && (
        <span
          className={cn(
            "grid size-6 shrink-0 place-items-center rounded-full",
            state === "good"
              ? "bg-accent text-accent-foreground"
              : "bg-primary text-primary-foreground",
          )}
        >
          <Check className="size-3.5" strokeWidth={3.5} />
        </span>
      )}
    </motion.button>
  );
}

export function CTA({
  children,
  onClick,
  variant = "gold",
  className,
}: {
  children: ReactNode;
  onClick?: () => void;
  variant?: "gold" | "mint" | "ghost";
  className?: string | undefined;
}) {
  return (
    <motion.button
      type="button"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className={cn(
        "mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3 text-[0.9rem] font-bold tracking-tight transition-transform lg:mt-6 lg:py-4 lg:text-[1rem]",
        variant === "gold" &&
          "bg-[image:var(--gradient-gold)] text-primary-foreground [box-shadow:var(--shadow-gold)]",
        variant === "mint" &&
          "bg-[image:var(--gradient-mint)] text-accent-foreground [box-shadow:var(--shadow-mint)]",
        variant === "ghost" && "border border-hairline bg-surface text-foreground",
        className,
      )}
    >
      {children}
      {variant !== "ghost" && <ArrowRight className="size-[18px]" strokeWidth={3} />}
    </motion.button>
  );
}

export function Hex({
  label,
  color = "gold",
  size = 120,
}: {
  label: string;
  color?: "gold" | "mint" | "silver";
  size?: number;
}) {
  const stroke =
    color === "mint"
      ? "hsl(var(--accent))"
      : color === "silver"
        ? "oklch(0.86 0.01 95)"
        : "hsl(var(--primary))";
  return (
    <svg viewBox="0 0 120 120" width={size} height={size} fill="none" aria-hidden>
      <motion.polygon
        points="60,6 108,33 108,87 60,114 12,87 12,33"
        stroke={stroke}
        strokeWidth={3}
        strokeLinejoin="round"
        initial={{ pathLength: 0, opacity: 0 }}
        animate={{ pathLength: 1, opacity: 1 }}
        transition={{ duration: 1 }}
      />
      <motion.polygon
        points="60,26 90,43 90,77 60,94 30,77 30,43"
        fill={stroke}
        fillOpacity={0.12}
        stroke={stroke}
        strokeWidth={1.5}
        initial={{ scale: 0.7, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.35, type: "spring", stiffness: 200, damping: 14 }}
        style={{ transformOrigin: "60px 60px" }}
      />
      <text
        x="60"
        y="67"
        textAnchor="middle"
        fontFamily="var(--font-mono)"
        fontSize="15"
        fontWeight="600"
        fill={stroke}
        letterSpacing="2"
      >
        {label}
      </text>
    </svg>
  );
}

export function Marquee({ items }: { items: string[] }) {
  const row = [...items, ...items];
  return (
    <div className="mt-4 overflow-hidden [mask-image:linear-gradient(90deg,transparent,black_12%,black_88%,transparent)]">
      <div className="marquee-track gap-2">
        {row.map((x, i) => (
          <span
            key={i}
            className="shrink-0 rounded-full border border-hairline bg-surface px-3 py-1.5 font-mono text-[11px] text-muted-foreground"
          >
            {x}
          </span>
        ))}
      </div>
    </div>
  );
}

export function Screen({ children }: { children: ReactNode }) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 22 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -14 }}
      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      className="pb-12 lg:pb-16"
    >
      {children}
    </motion.section>
  );
}

export function Counter({
  to,
  format = (v: number) => String(v),
  className,
  duration = 900,
}: {
  to: number;
  // eslint-disable-next-line no-unused-vars
  format?: (v: number) => string;
  className?: string | undefined;
  duration?: number;
}) {
  return (
    <motion.span
      className={className}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      ref={(node) => {
        if (!node) return;
        const start = performance.now();
        const tick = (now: number) => {
          const t = Math.min(1, (now - start) / duration);
          const e = 1 - Math.pow(1 - t, 3);
          node.textContent = format(Math.round(to * e));
          if (t < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      }}
    >
      {format(to)}
    </motion.span>
  );
}

/* ---------- AI check · game primitives ---------- */

/* Header for a check game: "MINDSET" + "GAME 1 OF 6" + the question */
export function GameHead({
  dim,
  n,
  total,
  children,
}: {
  dim: string;
  n: number;
  total: number;
  children: ReactNode;
}) {
  return (
    <>
      <Eyebrow right={`GAME ${n} OF ${total}`}>{dim}</Eyebrow>
      <Headline>{children}</Headline>
    </>
  );
}

/* One-line ruling that lands after an interaction. Named Ruling, not Verdict,
   because steps-close already exports a Verdict card. */
export function Ruling({
  children,
  tone = "mint",
  label,
}: {
  children: ReactNode;
  tone?: "mint" | "gold" | "red";
  label?: string | undefined;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className={cn(
        "mt-3 rounded-xl border p-3 text-[0.86rem] leading-relaxed",
        tone === "mint" && "border-accent/35 bg-accent/10",
        tone === "gold" && "border-primary/35 bg-primary/10",
        tone === "red" && "border-destructive/40 bg-destructive/10",
      )}
    >
      {label ? (
        <span className="mr-1.5 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          {label}
        </span>
      ) : null}
      {children}
    </motion.div>
  );
}

export function Chip({
  children,
  tone = "idle",
  className,
}: {
  children: ReactNode;
  tone?: "idle" | "gold" | "mint" | "red";
  className?: string | undefined;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-mono text-[11px] tracking-wide",
        tone === "idle" && "border-hairline bg-surface text-muted-foreground",
        tone === "gold" && "border-primary/45 bg-primary/12 text-primary-soft",
        tone === "mint" && "border-accent/45 bg-accent/12 text-accent-soft",
        tone === "red" && "border-destructive/45 bg-destructive/12 text-foreground",
        className,
      )}
    >
      {children}
    </span>
  );
}

export function ProgressDots({ done, total }: { done: number; total: number }) {
  return (
    <div className="mt-3 flex gap-1">
      {Array.from({ length: total }).map((_, k) => (
        <span
          key={k}
          className={cn(
            "h-1 flex-1 rounded-full transition-colors",
            k < done ? "bg-primary" : "bg-surface-2",
          )}
        />
      ))}
    </div>
  );
}

/* Countdown ring for the myth shredder */
export function TimerRing({
  seconds,
  running,
  onExpire,
  keyId,
}: {
  seconds: number;
  running: boolean;
  onExpire: () => void;
  keyId: string | number;
}) {
  const [left, setLeft] = useState(seconds);
  const fired = useRef(false);

  useEffect(() => {
    setLeft(seconds);
    fired.current = false;
  }, [keyId, seconds]);

  useEffect(() => {
    if (!running) return;
    const start = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const l = Math.max(0, seconds - (now - start) / 1000);
      setLeft(l);
      if (l <= 0) {
        if (!fired.current) {
          fired.current = true;
          onExpire();
        }
        return;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [running, keyId, seconds, onExpire]);

  const r = 16;
  const c = 2 * Math.PI * r;
  return (
    <span className="relative grid size-11 shrink-0 place-items-center">
      <svg viewBox="0 0 40 40" className="absolute size-11 -rotate-90" aria-hidden>
        <circle cx="20" cy="20" r={r} stroke="hsl(var(--surface-2))" strokeWidth="3" fill="none" />
        <circle
          cx="20"
          cy="20"
          r={r}
          stroke={left < 3 ? "hsl(var(--destructive))" : "hsl(var(--accent))"}
          strokeWidth="3"
          fill="none"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - left / seconds)}
          strokeLinecap="round"
        />
      </svg>
      <span className="font-mono text-sm font-bold">{Math.ceil(left)}</span>
    </span>
  );
}

/* Spider chart. Each axis value is 0..1. */
export function Radar({ axes }: { axes: { label: string; value: number }[] }) {
  const cx = 100;
  const cy = 105;
  const R = 78;
  const pt = (i: number, r: number) => {
    const a = -Math.PI / 2 + (i * 2 * Math.PI) / axes.length;
    return [cx + r * Math.cos(a), cy + r * Math.sin(a)] as const;
  };
  const poly = (r: number) => axes.map((_, i) => pt(i, r).join(",")).join(" ");
  const data = axes.map((a, i) => pt(i, Math.max(0.06, a.value) * R).join(",")).join(" ");

  return (
    <svg
      viewBox="-45 -4 290 212"
      className="mx-auto mt-4 w-[270px] max-w-full"
      role="img"
      aria-label={`Readiness radar: ${axes.map((a) => `${a.label} ${Math.round(a.value * 100)}%`).join(", ")}`}
    >
      {[0.33, 0.66, 1].map((f) => (
        <polygon
          key={f}
          points={poly(R * f)}
          fill="none"
          stroke="hsl(var(--hairline))"
          strokeWidth="1"
        />
      ))}
      {axes.map((a, i) => {
        const [x, y] = pt(i, R);
        return <line key={a.label} x1={cx} y1={cy} x2={x} y2={y} stroke="hsl(var(--hairline))" />;
      })}
      <motion.polygon
        points={data}
        fill="color-mix(in oklab, hsl(var(--primary)) 35%, transparent)"
        stroke="hsl(var(--primary))"
        strokeWidth="2.5"
        strokeLinejoin="round"
        initial={{ opacity: 0, scale: 0.4 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ type: "spring", stiffness: 160, damping: 16, delay: 0.3 }}
        style={{ transformOrigin: `${cx}px ${cy}px` }}
      />
      {axes.map((a, i) => {
        const [x, y] = pt(i, R + 18);
        return (
          <text
            key={a.label}
            x={x}
            y={y + 4}
            textAnchor="middle"
            fontFamily="var(--font-mono)"
            fontSize="10"
            fill="hsl(var(--muted-foreground))"
            letterSpacing="1"
          >
            {a.label.toUpperCase()}
          </text>
        );
      })}
    </svg>
  );
}
